/** Tier row keys (order matters for UI). */
export const TIER_KEYS = ['S', 'A', 'B', 'C', 'D', 'E', 'F']

/** Droppable ids (must never equal a BGG objectid — use prefix). */
export const DROP_POOL = 'rank:drop:pool'
export const DROP_EXCLUDED = 'rank:drop:excluded'
export const dropIdForTier = (tierKey) => `rank:drop:tier:${tierKey}`

export const TIER_META = [
	{ key: 'S', label: 'S', colorScheme: 'red' },
	{ key: 'A', label: 'A', colorScheme: 'orange' },
	{ key: 'B', label: 'B', colorScheme: 'pink' },
	{ key: 'C', label: 'C', colorScheme: 'green' },
	{ key: 'D', label: 'D', colorScheme: 'purple' },
	{ key: 'E', label: 'E', colorScheme: 'cyan' },
	{ key: 'F', label: 'F', colorScheme: 'blue' },
]

export function emptyTiers() {
	return Object.fromEntries(TIER_KEYS.map((k) => [k, []]))
}

/** @typedef {{ pool: string[], tiers: Record<string, string[]>, excluded: string[] }} RankState */

/** @returns {RankState} */
export function createInitialRankStateFromCollectionIds(collectionIds) {
	const deduped = [...new Set(collectionIds.map(String))]
	return {
		pool: deduped,
		tiers: emptyTiers(),
		excluded: [],
	}
}

/** Deep-enough clone for drag cancel / drop-outside restore. */
export function cloneRankState(state) {
	return {
		pool: [...state.pool],
		excluded: [...state.excluded],
		tiers: Object.fromEntries(TIER_KEYS.map((k) => [k, [...(state.tiers[k] || [])]])),
	}
}

function getList(state, container) {
	if (container === 'pool') return state.pool
	if (container === 'excluded') return state.excluded
	return state.tiers[container] || []
}

function setList(state, container, list) {
	if (container === 'pool') return { ...state, pool: list }
	if (container === 'excluded') return { ...state, excluded: list }
	return {
		...state,
		tiers: { ...state.tiers, [container]: list },
	}
}

/** Which logical container holds this item id? */
export function findItemContainer(state, itemId) {
	const id = String(itemId)
	if (state.pool.includes(id)) return 'pool'
	if (state.excluded.includes(id)) return 'excluded'
	for (const k of TIER_KEYS) {
		if (state.tiers[k].includes(id)) return k
	}
	return null
}

/** Map droppable id to container key, or null. */
export function dropTargetToContainer(overId) {
	if (overId === DROP_POOL) return 'pool'
	if (overId === DROP_EXCLUDED) return 'excluded'
	if (typeof overId === 'string' && overId.startsWith('rank:drop:tier:')) {
		const k = overId.slice('rank:drop:tier:'.length)
		if (TIER_KEYS.includes(k)) return k
	}
	return null
}

/**
 * Resolve container for drag-over target (either another item or a droppable).
 * @returns {string|null}
 */
export function resolveOverContainer(state, overId) {
	if (!overId) return null
	const asItem = findItemContainer(state, overId)
	if (asItem) return asItem
	return dropTargetToContainer(overId)
}

function sameIdListOrder(a, b) {
	if (a.length !== b.length) return false
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false
	}
	return true
}

function rankStateListsEqual(a, b) {
	if (!sameIdListOrder(a.pool, b.pool) || !sameIdListOrder(a.excluded, b.excluded)) return false
	for (const k of TIER_KEYS) {
		if (!sameIdListOrder(a.tiers[k], b.tiers[k])) return false
	}
	return true
}

/**
 * Move active item toward hover/drop target (item id or droppable id).
 * Safe to call from onDragOver repeatedly; returns `state` when nothing changes.
 * @param {RankState} state
 * @param {string} activeId
 * @param {string} overId
 * @returns {RankState}
 */
export function moveItemTowardOver(state, activeId, overId) {
	if (!overId || activeId === overId) return state

	const activeC = findItemContainer(state, activeId)
	if (!activeC) return state

	const overC = resolveOverContainer(state, overId)
	if (!overC) return state

	const activeList = getList(state, activeC)
	const oldIndex = activeList.indexOf(activeId)
	if (oldIndex === -1) return state

	const overIsItem = !!findItemContainer(state, overId)

	if (activeC === overC) {
		const list = [...activeList]
		list.splice(oldIndex, 1)
		let insertAt
		if (overIsItem) {
			insertAt = list.indexOf(overId)
			if (insertAt === -1) insertAt = list.length
		} else {
			insertAt = list.length
		}
		if (insertAt < 0) insertAt = 0
		if (insertAt > list.length) insertAt = list.length
		list.splice(insertAt, 0, activeId)
		if (sameIdListOrder(list, activeList)) return state
		return setList(state, activeC, list)
	}

	const src = [...activeList]
	src.splice(oldIndex, 1)
	const dest = [...getList(state, overC)]
	let insertAt
	if (overIsItem) {
		insertAt = dest.indexOf(overId)
		if (insertAt === -1) insertAt = dest.length
	} else {
		insertAt = dest.length
	}
	if (insertAt < 0) insertAt = 0
	if (insertAt > dest.length) insertAt = dest.length
	dest.splice(insertAt, 0, activeId)

	let next = setList(state, activeC, src)
	next = setList(next, overC, dest)
	if (rankStateListsEqual(state, next)) return state
	return next
}

/**
 * Apply a completed drag (active = item id, over = item id or droppable id).
 * @param {RankState} state
 * @param {string} activeId
 * @param {string} overId
 * @returns {RankState}
 */
export function applyDragEnd(state, activeId, overId) {
	return moveItemTowardOver(state, activeId, overId)
}

export function gameObjectId(game) {
	if (!game) return ''
	return String(game['@_objectid'] ?? '')
}

export function buildItemsRecordFromCollection(collection) {
	/** @type {Record<string, { objectid: string, name: string, thumbnail: unknown, image: unknown }>} */
	const items = {}
	if (!collection || !collection.length) return items
	for (const game of collection) {
		const id = gameObjectId(game)
		if (!id) continue
		const nameNode = game.name
		const name =
			typeof nameNode === 'object' && nameNode != null && nameNode['#text'] != null
				? String(nameNode['#text'])
				: String(nameNode ?? '')
		items[id] = {
			objectid: id,
			name,
			thumbnail: game.thumbnail,
			image: game.image,
		}
	}
	return items
}

function legacyIdForName(name) {
	return `legacy:${String(name)}`
}

/**
 * Normalize pasted JSON into v2 shape. Throws on invalid input.
 * @returns {{ schemaVersion: 2, pool: string[], tiers: Record<string, string[]>, excluded: string[], items: Record<string, object> }}
 */
export function parseRankImport(json) {
	const parsed = typeof json === 'string' ? JSON.parse(json) : json
	if (!parsed || typeof parsed !== 'object') throw new Error('Invalid JSON')

	if (parsed.schemaVersion === 2) {
		const pool = Array.isArray(parsed.pool) ? parsed.pool.map(String) : []
		const excluded = Array.isArray(parsed.excluded) ? parsed.excluded.map(String) : []
		const tiers = emptyTiers()
		for (const k of TIER_KEYS) {
			if (Array.isArray(parsed.tiers?.[k])) tiers[k] = parsed.tiers[k].map(String)
		}
		const items = parsed.items && typeof parsed.items === 'object' ? { ...parsed.items } : {}
		for (const id of Object.keys(items)) {
			const row = items[id]
			if (row && typeof row === 'object') {
				items[id] = {
					objectid: row.objectid != null ? String(row.objectid) : id,
					name: row.name != null ? String(row.name) : '',
					thumbnail: row.thumbnail,
					image: row.image,
				}
			}
		}
		return { schemaVersion: 2, pool, excluded, tiers, items }
	}

	if (Array.isArray(parsed)) {
		return migrateV1ArrayToV2(parsed)
	}

	throw new Error('Unrecognized export format')
}

function migrateV1ArrayToV2(rows) {
	const tiers = emptyTiers()
	const excluded = []
	const items = {}

	for (const row of rows) {
		if (!row || typeof row !== 'object') continue
		const tier = row.tier != null ? String(row.tier) : ''
		const name = row.name != null ? String(row.name) : ''
		const id = row.objectid != null ? String(row.objectid) : legacyIdForName(name || 'unknown')
		items[id] = {
			objectid: row.objectid != null ? String(row.objectid) : null,
			name,
			thumbnail: row.thumbnail,
			image: row.image,
		}
		if (tier === 'N') excluded.push(id)
		else if (TIER_KEYS.includes(tier)) tiers[tier].push(id)
	}

	for (const k of TIER_KEYS) {
		tiers[k].sort((a, b) => (items[a]?.name || '').localeCompare(items[b]?.name || '', undefined, { sensitivity: 'base' }))
	}

	return {
		schemaVersion: 2,
		pool: [],
		excluded,
		tiers,
		items,
	}
}

/**
 * Full document for export modal / file.
 * @param {RankState} rankState
 * @param {Record<string, object>} itemsById
 */
export function serializeRankExport(rankState, itemsById) {
	return {
		schemaVersion: 2,
		pool: [...rankState.pool],
		excluded: [...rankState.excluded],
		tiers: { ...emptyTiers(), ...Object.fromEntries(TIER_KEYS.map((k) => [k, [...(rankState.tiers[k] || [])]])) },
		items: { ...itemsById },
	}
}

/** RankState slice from v2 import (no items). */
export function rankStateFromV2(v2) {
	return {
		pool: [...(v2.pool || [])],
		excluded: [...(v2.excluded || [])],
		tiers: { ...emptyTiers(), ...Object.fromEntries(TIER_KEYS.map((k) => [k, [...(v2.tiers?.[k] || [])]])) },
	}
}

/** All ids that appear anywhere in rank state (for dedupe checks). */
export function allRankedIds(state) {
	const s = new Set()
	for (const id of state.pool) s.add(id)
	for (const id of state.excluded) s.add(id)
	for (const k of TIER_KEYS) {
		for (const id of state.tiers[k]) s.add(id)
	}
	return s
}

/**
 * Minimal BGG-shaped objects for CollectionContext after loading a v2 export from slots.
 * @param {RankState} state
 * @param {Record<string, { objectid?: string, name?: string, thumbnail?: unknown, image?: unknown }>} itemsById
 */
export function buildCollectionStubsForRanking(state, itemsById) {
	const ids = [...allRankedIds(state)]
	return ids.map((id) => {
		const row = itemsById[id] || {}
		const name = row.name != null ? String(row.name) : ''
		return {
			'@_objectid': id,
			name: { '#text': name },
			thumbnail: row.thumbnail,
			image: row.image,
			stats: {
				'@_minplayers': '1',
				'@_maxplayers': '99',
				'@_playingtime': '0',
			},
			yearpublished: { '#text': '' },
		}
	})
}
